import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { IonIcon } from '@ionic/angular/standalone';

@Component({
  selector: 'app-offline-banner',
  standalone: true,
  imports: [IonIcon],
  template: `
    @if (isOffline()) {
      <div class="offline-banner" [class.dismissing]="isDismissing()">
        <ion-icon name="cloud-offline-outline"></ion-icon>
        <span>{{ isOffline() ? 'No internet connection' : '' }}</span>
      </div>
    }
  `,
  styles: [
    `
      .offline-banner {
        position: fixed;
        top: calc(env(safe-area-inset-top) + 48px);
        left: 16px;
        right: 16px;
        z-index: 9999;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 16px;
        background: var(--dp-danger-500);
        color: #ffffff;
        border-radius: var(--dp-radius-md);
        font-size: 13px;
        font-weight: 500;
        box-shadow: var(--dp-shadow-lg);
        animation: bannerSlideDown 0.3s ease-out;
      }

      .offline-banner.dismissing {
        animation: bannerSlideUp 0.3s ease-in forwards;
      }

      .offline-banner ion-icon {
        font-size: 18px;
        flex-shrink: 0;
      }

      @keyframes bannerSlideDown {
        from {
          opacity: 0;
          transform: translateY(-16px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes bannerSlideUp {
        from {
          opacity: 1;
          transform: translateY(0);
        }
        to {
          opacity: 0;
          transform: translateY(-16px);
        }
      }
    `,
  ],
})
export class OfflineBannerComponent implements OnInit, OnDestroy {
  readonly isOffline = signal(false);
  readonly isDismissing = signal(false);
  private listenerHandle: string | null = null;

  async ngOnInit(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      // Web fallback
      this.isOffline.set(!navigator.onLine);
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
      return;
    }

    const status = await Network.getStatus();
    this.isOffline.set(!status.connected);

    await Network.addListener('networkStatusChange', (status) => {
      if (status.connected && this.isOffline()) {
        this.isDismissing.set(true);
        setTimeout(() => {
          this.isOffline.set(false);
          this.isDismissing.set(false);
        }, 300);
      } else if (!status.connected) {
        this.isOffline.set(true);
      }
    });
  }

  async ngOnDestroy(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Network.removeAllListeners();
    } else {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }
  }

  private handleOnline = (): void => {
    this.isDismissing.set(true);
    setTimeout(() => {
      this.isOffline.set(false);
      this.isDismissing.set(false);
    }, 300);
  };

  private handleOffline = (): void => {
    this.isOffline.set(true);
  };
}
